// THE ONE LIST of what this portal's sidebar offers, and which platform
// permission opens each entry.
//
// Both personas the portal signs in read from here: the Super Admin (who passes
// every check) and their PLATFORM STAFF (who pass only what their role grants —
// utils/platformPermissions.js). The sidebar draws the sections that survive
// the filter; App.jsx's "/" landing walks the same order to pick a staff
// member's first page. One list on purpose: the previous shape kept a
// hand-written super-admin rail and a separate staff rail, and the two
// drifted — a role granted every permission still saw only the two support
// queues, because nobody had copied the other sections across. A new screen
// is now added HERE, with its key, and every consumer follows.
//
// The keys must match what the API checks for the same calls
// (Backend/config/platformPermissions.js — served by /api/platform-roles/
// catalogue). A view page carries the `.view` key; the API refuses its write
// calls without `.manage`, and the page hides those buttons via
// hooks/useCanManage.js.
//
// Per-item fields:
//   perm           — the permission that opens it (Super Admin always passes).
//   queue          — a support queue instead of a permission: 'tenant' | 'patient'.
//                    Checked with canViewSupportQueue, which honours the
//                    `support.queueScoping` feature flag.
//   superAdminOnly — no catalogue key exists; hidden from every staff role.
//   labelKey / defaultLabel — i18n key in the 'sidebar' namespace + fallback.
//   activePaths / activePrefix — see Sidebar.jsx's isItemActive.
//
// DISTINCT ICON PER NAV ITEM — the product owner's ask: no icon is reused
// within this menu. Section order is deliberate — do not sort.

import {
  Building2, UserCog, LifeBuoy, Users, LayoutGrid, MessagesSquare, FileChartColumn, Sigma, Send,
  Tag, IndianRupee, Layers, Stethoscope, FlaskConical, Scan, ClipboardList, Flag, Briefcase,
  Headset, Languages, ShieldAlert, LogIn, History, CalendarClock, BrainCircuit, Network, Radio,
  UsersRound, KeyRound, Settings2, LayoutDashboard, FileChartLine, ChartColumnStacked, MailWarning,
  Shapes, HeartPulse, Mail,
} from 'lucide-react';

export const PLATFORM_NAV = [
  // DASHBOARD — the platform at a glance (KPIs, growth, alerts, support
  // load). First on purpose: it is the Super Admin's landing page, and a
  // staff role granted `reports.view` lands here too (App.jsx's "/" walks
  // this list in order).
  {
    id: 'sec-dashboard',
    labelKey: 'sidebar.dashboard',
    defaultLabel: 'Dashboard',
    items: [
      { id: 'nav-dashboard', path: '/dashboard', labelKey: 'sidebar.dashboard', defaultLabel: 'Dashboard', icon: LayoutDashboard, perm: 'reports.view', activePaths: ['/dashboard'] },
    ],
  },

  // TENANTS — everything about the hospitals on the platform: the tenants
  // themselves, the admins who run them, and the support queue every tenant
  // user (admin, doctor, staff, isolated-tenant patient) raises into.
  {
    id: 'sec-tenants',
    labelKey: 'sidebar.tenants',
    defaultLabel: 'Tenants',
    items: [
      { id: 'nav-tenants', path: '/tenants', labelKey: 'sidebar.tenants', defaultLabel: 'Tenants', icon: Building2, perm: 'tenants.view', activePaths: ['/tenants'] },
      // The admin LIST is a read (GET /api/admins → tenants.view); Add /
      // delete / status need tenants.manage and are hidden on the page.
      // /manageAdminProfile is where the list's eye icon opens an admin.
      { id: 'nav-admins', path: '/admins', labelKey: 'sidebar.admins', defaultLabel: 'Admins', icon: UserCog, perm: 'tenants.view', activePaths: ['/admins', '/manageAdminProfile'] },
      // Tenant report / count read /api/platform/reports/* behind the
      // `reports.view` key — the same key as the dashboard, not tenants.view.
      // Their paths say 'tenant-' without the 's', so the Tenants entry's
      // exact-match activePaths cannot swallow them.
      { id: 'nav-tenant-report', path: '/tenant-report', labelKey: 'sidebar.tenant_report', defaultLabel: 'Tenant Report', icon: FileChartLine, perm: 'reports.view' },
      { id: 'nav-tenant-count', path: '/tenant-count', labelKey: 'sidebar.tenant_count', defaultLabel: 'Tenant Count', icon: ChartColumnStacked, perm: 'reports.view' },
      // "Tenant Support" is who it serves; the /platform-support path, id and
      // socket wiring keep the old name on purpose.
      { id: 'nav-platform-support', path: '/platform-support', labelKey: 'sidebar.tenant_support', defaultLabel: 'Tenant Support', icon: LifeBuoy, queue: 'tenant', activePaths: ['/platform-support'] },
      // Custom-plan enquiries from the public sites and from doctors — the
      // leads that become tenants, which is why they are listed here. No
      // catalogue key: the API (contactInquiryRoutes.js) answers super_admin
      // only, because one list holds every hospital's enquiries, so a staff
      // role granted tenants.view would see the link and get a 403. Same
      // shape as Email Queue and Settings.
      { id: 'nav-contact-inquiries', path: '/contact-inquiries', labelKey: 'sidebar.contact_inquiries', defaultLabel: 'Contact Requests', icon: Mail, superAdminOnly: true, activePaths: ['/contact-inquiries'] },
    ],
  },

  // MOC & PATIENTS — the patient-facing layer: the patient roster, what the
  // patient app shows, its support queue, and the patient reports/invites.
  //
  // ONE Patients entry, not two (owner's merge, Aug 2026): "Mitra One Care"
  // (the global identities) and "Patients" (per-hospital rows) listed the
  // same people twice, so the identity list took the name and /patients.
  //
  // `activePrefix` keeps the entry lit on /patients/:mocAccountId. It cannot
  // swallow the report pages — their paths say 'patient-' without the 's'.
  {
    id: 'sec-moc',
    labelKey: 'sidebar.moc_patients',
    defaultLabel: 'MOC & Patients',
    items: [
      { id: 'nav-patients', path: '/patients', labelKey: 'sidebar.patient', defaultLabel: 'Patients', icon: Users, perm: 'patients.view', activePrefix: '/patients' },
      // Opens on patients.view; the Save that writes needs patients.manage.
      { id: 'nav-moc-services', path: '/moc/services', labelKey: 'sidebar.moc_services', defaultLabel: 'Services', icon: LayoutGrid, perm: 'patients.view' },
      { id: 'nav-moc-support', path: '/moc/support', labelKey: 'sidebar.patient_support', defaultLabel: 'Patient Support', icon: MessagesSquare, queue: 'patient' },
      { id: 'nav-patient-report', path: '/patient-report', labelKey: 'sidebar.patient_report', defaultLabel: 'Patient Report', icon: FileChartColumn, perm: 'patients.view' },
      { id: 'nav-patient-count', path: '/patient-count', labelKey: 'sidebar.patient_count', defaultLabel: 'Patient Count', icon: Sigma, perm: 'patients.view' },
      { id: 'nav-patient-invite', path: '/patient-invite', labelKey: 'sidebar.patient_invite', defaultLabel: 'Send Invite', icon: Send, perm: 'invites.send' },
    ],
  },

  // MASTER — GLOBAL catalogues: one shared specialty / country / profession /
  // language list for every hospital on the platform. A tenant admin editing
  // one would change it for every other hospital, and deleting a country
  // orphans every doctor and patient referencing it — which is why only the
  // platform side (masters.view to open, masters.manage to write) sees them.
  //
  // FOUR GROUPS IN ONE LIST — do not re-sort alphabetically. Order: what
  // defines a tenant (Tenant setup), then the clinical catalogues, then
  // reference data, then editable content.
  {
    id: 'sec-master',
    labelKey: 'sidebar.master',
    defaultLabel: 'Master',
    items: [
      // Tenant setup — what a tenant IS and what it costs. Feature Price is
      // internal commercial data: the API gates it on READ as well as write,
      // and to tenants.manage rather than masters.view (rollout doc), because
      // the tenant form reads the same list for its plan quote.
      { id: 'nav-master-tenant-type', path: '/tenant-type', labelKey: 'sidebar.tenant_type', defaultLabel: 'Tenant Type', icon: Tag, perm: 'masters.view' },
      { id: 'nav-master-feature-price', path: '/feature-price', labelKey: 'sidebar.feature_price', defaultLabel: 'Feature Price', icon: IndianRupee, perm: 'tenants.manage' },

      // Clinical catalogues. Specialities first (they classify the doctors),
      // then the world-level item catalogs, then the queue of tenant-proposed
      // additions to them.
      { id: 'nav-master-speciality-category', path: '/speciality-category', labelKey: 'sidebar.speciality_category', defaultLabel: 'Speciality Category', icon: Layers, perm: 'masters.view' },
      { id: 'nav-master-speciality', path: '/speciality', labelKey: 'sidebar.speciality', defaultLabel: 'Speciality', icon: Stethoscope, perm: 'masters.view' },
      // The bundled icon library the two masters above pick from. Read-only
      // by nature — a new icon is a code release, not a form — so it opens on
      // masters.view and has no write key at all.
      { id: 'nav-master-speciality-icons', path: '/speciality-icons', labelKey: 'sidebar.speciality_icons', defaultLabel: 'Specialty icons', icon: Shapes, perm: 'masters.view' },
      // The patient-language layer on top of the two masters above: the
      // everyday complaint ("Ear, nose & throat") mapped to the specialties
      // that treat it. Listed right after them because it is only meaningful
      // once those exist.
      { id: 'healthConcerns', path: '/master/health-concerns', labelKey: 'sidebar.healthConcerns', defaultLabel: 'Health concerns', icon: HeartPulse, perm: 'masters.view' },
      // No Medicines entry (owner, 10 Oct 2026): there is no medicine master.
      // Every doctor and every pharmacy builds its own list; the platform
      // does nothing with medicines.
      { id: 'nav-master-lab-tests', path: '/master-catalog/lab_test', labelKey: 'sidebar.master_lab_tests', defaultLabel: 'Lab Tests', icon: FlaskConical, perm: 'masters.view' },
      { id: 'nav-master-procedures', path: '/master-catalog/imaging_procedure', labelKey: 'sidebar.master_procedures', defaultLabel: 'Radiology Procedures', icon: Scan, perm: 'masters.view' },
      { id: 'nav-master-proposals', path: '/catalog-proposals', labelKey: 'sidebar.catalog_proposals', defaultLabel: 'Catalog Proposals', icon: ClipboardList, perm: 'masters.view' },

      // Reference. Set once at onboarding and then almost never reopened.
      { id: 'nav-master-country', path: '/country', labelKey: 'sidebar.country', defaultLabel: 'Country', icon: Flag, perm: 'masters.view' },
      { id: 'nav-master-profession', path: '/profession', labelKey: 'sidebar.profession', defaultLabel: 'Profession', icon: Briefcase, perm: 'masters.view' },

      // Content. Text the platform shows, not data it classifies by. (Support
      // here is the ticket CATEGORY master, not the queues above.)
      { id: 'nav-master-support', path: '/master-support', labelKey: 'sidebar.support', defaultLabel: 'Support', icon: Headset, perm: 'masters.view' },
      { id: 'nav-master-translation', path: '/translation', labelKey: 'sidebar.translation', defaultLabel: 'Translation', icon: Languages, perm: 'masters.view' },
    ],
  },

  // AUDIT & SECURITY — ORDERED BY WHAT YOU OPEN FIRST, not by log type: the
  // human-readable trails lead, and the transport-level transcripts (API,
  // Socket) sit last.
  {
    id: 'sec-audit',
    labelKey: 'sidebar.audit_security',
    defaultLabel: 'Audit & Security',
    items: [
      { id: 'nav-audit-security', path: '/audit/security', labelKey: 'sidebar.security_monitoring', defaultLabel: 'Security Monitoring', icon: ShieldAlert, perm: 'audit.view' },
      { id: 'nav-audit-login', path: '/audit/login', labelKey: 'sidebar.login_logs', defaultLabel: 'Login Logs', icon: LogIn, perm: 'audit.view' },
      { id: 'nav-audit-activity', path: '/audit/activity', labelKey: 'sidebar.activity_logs', defaultLabel: 'Activity Logs', icon: History, perm: 'audit.view' },
      { id: 'nav-audit-appointments', path: '/audit/appointments', labelKey: 'sidebar.appointment_logs', defaultLabel: 'Appointment Logs', icon: CalendarClock, perm: 'audit.view' },
      { id: 'nav-audit-ai', path: '/audit/ai', labelKey: 'sidebar.ai_log', defaultLabel: 'AI Log', icon: BrainCircuit, perm: 'audit.view' },
      { id: 'nav-audit-api', path: '/audit/api', labelKey: 'sidebar.api_logs', defaultLabel: 'API Logs', icon: Network, perm: 'audit.view' },
      { id: 'nav-audit-socket', path: '/audit/socket', labelKey: 'sidebar.socket_logs', defaultLabel: 'Socket Logs', icon: Radio, perm: 'audit.view' },
      // Outbound mail queue (Redis-backed) — an ops screen with no catalogue
      // key: requireSuperAdmin on the API, so Super Admin only, like Settings.
      { id: 'nav-email-queue', path: '/email-queue', labelKey: 'sidebar.email_queue', defaultLabel: 'Email Queue', icon: MailWarning, superAdminOnly: true },
    ],
  },

  // ACCOUNT — the platform's own team, its roles, and Settings. Users and
  // Roles are the two keys the catalogue calls "Platform team"; Settings
  // (feature flags, payment holds) has no key and stays the Super Admin's.
  {
    id: 'sec-account',
    labelKey: 'sidebar.account',
    defaultLabel: 'Account',
    items: [
      { id: 'nav-users', path: '/users', labelKey: 'sidebar.all_users', defaultLabel: 'All users', icon: UsersRound, perm: 'users.manage' },
      { id: 'nav-user-roles', path: '/user-roles', labelKey: 'sidebar.user_roles', defaultLabel: 'Roles', icon: KeyRound, perm: 'roles.manage' },
      { id: 'nav-settings', path: '/settings', labelKey: 'sidebar.settings', defaultLabel: 'Settings', icon: Settings2, superAdminOnly: true },
    ],
  },
];

/** Flat run of every item, in rail order. */
export const PLATFORM_NAV_ITEMS = PLATFORM_NAV.flatMap((s) => s.items);
